import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addCombiner } from '../../../store/slices/databaseSlice';
import { combinersService } from '../../../services';
import { InverterBrand, InverterType, Combiner } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface CombinerForm {
  inverterBrandId: string;
  name: string;
}

export const CombinerManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);

  const [form, setForm] = useState<CombinerForm>({ inverterBrandId: '', name: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localCombiners, setLocalCombiners] = useState<Combiner[]>([]);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{ id: string; label: string } | null>(null);

  useEffect(() => {
    setLocalCombiners(dbData.combiners);
  }, [dbData.combiners]);

  // Only show microinverter brands (Enphase, QCells, etc.)
  const brandOptions = dbData.inverter_brands
    .filter((brand: InverterBrand) => brand.inverter_type === InverterType.MICROINVERTER)
    .map((brand: InverterBrand) => ({
      id: brand.id,
      label: brand.name,
    }));

  const handleBrandSelect = (option: { id: string; label: string } | null) => {
    setSelectedBrandOption(option);
    setForm((prev) => ({ ...prev, inverterBrandId: option?.id || '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.inverterBrandId) {
      toast('Please select an inverter brand', TOAST_WARNING_CONFIG);
      return;
    }
    if (!form.name.trim()) {
      toast('Please enter a combiner name', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newCombiner = await combinersService.create({
        name: form.name.trim(),
        inverter_brand_id: form.inverterBrandId,
      });

      dispatch(addCombiner(newCombiner));
      setLocalCombiners((prev) => [...prev, newCombiner]);
      setForm({ inverterBrandId: '', name: '' });
      setSelectedBrandOption(null);

      toast.success('Combiner created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating combiner:', error);
      toast.error('Failed to create combiner. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group combiners by brand for display
  const combinersByBrand = localCombiners.reduce<Record<string, Combiner[]>>((acc, combiner) => {
    const brandName = combiner.inverter_brand?.name || 'Unknown';
    if (!acc[brandName]) {
      acc[brandName] = [];
    }
    acc[brandName].push(combiner);
    return acc;
  }, {});

  return (
    <div className="p-4">
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">Add New Combiner</h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Inverter Brand *</label>
              <SearchDropdown
                options={brandOptions}
                onSelect={handleBrandSelect}
                placeholder="Select inverter brand..."
                value={selectedBrandOption}
                className="text-sm"
                inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                zIndex={1000}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Combiner Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., IQ Combiner 4C"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !form.inverterBrandId || !form.name.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create Combiner'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">Existing Combiners</h3>
            <span className="text-xs text-slate-500">{localCombiners.length} items</span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {Object.keys(combinersByBrand).length > 0 ? (
              <div className="space-y-3 p-2">
                {Object.entries(combinersByBrand).map(([brandName, combiners]) => (
                  <div key={brandName}>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      {brandName}
                    </div>
                    <div className="space-y-1.5">
                      {combiners.map((combiner) => (
                        <div
                          key={combiner.id}
                          className="flex items-center justify-between p-2.5 bg-emerald-50 text-emerald-800 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition-colors"
                        >
                          <span className="text-sm font-medium">{combiner.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">No combiners found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
